import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import ExcelJS from "exceljs";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { Download, Upload, AlertCircle, CheckCircle, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import FilterPills from "./admin/FilterPills";
import MissingSuppliersModal from "./admin/MissingSuppliersModal";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { preflightSuppliers, buildSupplierFixupRows } from "../lib/preflightSuppliers";
import type { LogRow } from "../types/Log";

const PAGE_SIZE = 25;
// "Download current" pages the BE 500 rows at a time, keeps every row in
// memory, then builds a single-sheet workbook in the browser. Since the
// Jobalots EU load (2026-10) the table holds ~2.2M products, so an
// unfiltered download would fire thousands of requests and exhaust the
// tab. The button only enables while the active filter's total is within
// this cap — 50k rows is ~100 requests and a workbook that builds in
// seconds; narrow by supplier / status / search to get under it.
const DOWNLOAD_MAX_ROWS = 50_000;

interface Product {
    sku: string;
    // Master SKU groups this product with other products that share the
    // same supplier or sales-channel identifier. Nullable: most products
    // don't have one. The dispatch scan resolver matches against this
    // column so a scanned master SKU finds the product the same way a
    // scanned unit SKU does.
    master_sku: string | null;
    title: string;
    barcode: string | null;
    image_url: string | null;
    // Pre-generated 224×224 variant of image_url. The picker hero
    // prefers this for fast handheld loads; falls back to image_url
    // when null. Populated either by the products upload pipeline or
    // by scripts/backfill_product_thumbnails.js for the historical
    // catalogue.
    thumbnail_url: string | null;
    weight_grams: number | null;
    length_cm: number | string | null;
    width_cm: number | string | null;
    height_cm: number | string | null;
    country_of_origin: string | null;
    commodity_code: string | null;
    supplier_name: string | null;
    ship_in_packaging: boolean;
    // When true the dispatch box recommender never offers a PLASTIC
    // mailbag for this item — cardboard mailbags and rigid boxes only.
    extra_protection: boolean;
    // Ships as a whole pallet (BE migration 202): its orders go to the
    // Pallet pick queue and out by pallet carrier. Set automatically for
    // SKUs created by Goods In › Pallets.
    ships_as_pallet: boolean;
    // Soft item (BE migration 250): clothing, costumes — in a PLASTIC
    // mailbag only its footprint has to fit, so a folded costume gets a P1.
    soft_item?: boolean;
    // Recommended retail price. Nullable — a product can be in the
    // catalogue before pricing is agreed. NUMERIC(12,2) on the DB side
    // arrives as a string from pg, hence the union.
    rrp: number | string | null;
    // Cost basis, inherited from the most recent manifest's unit_cost
    // (overwritten on every manifest create) but also editable here.
    // Nullable for legacy rows with no manifest yet. NUMERIC(12,4) →
    // arrives as a string from pg, same union as rrp.
    cost_price: number | string | null;
    // Per-channel sync toggles. Adding a new channel = one column on the
    // DB + one entry in CHANNEL_KEYS below. Default false on every row.
    sync_jobalots: boolean;
    sync_bargainfox: boolean;
    // Additional barcodes that resolve to this SKU on a scan. Stored in
    // sku_barcodes (master_sku = this sku, barcode = each entry). The
    // primary barcode (above) is the canonical identifier; extras are
    // alternates — relabel runs, multipack variants, supplier-specific
    // GTINs. Always returned as an array (empty when none).
    extra_barcodes: string[];
    archived_at?: string | null;
    created_at?: string;
    updated_at?: string;
}

// Sales channels the operator can opt this SKU into. Keep the FE-side
// label here so the modal renders without a separate i18n lookup per
// channel; new channels added on the BE need an entry here AND a
// boolean column on the products table.
const CHANNEL_KEYS = ["sync_jobalots", "sync_bargainfox"] as const;
type ChannelKey = typeof CHANNEL_KEYS[number];
const CHANNEL_LABELS: Record<ChannelKey, string> = {
    sync_jobalots:   "Jobalots",
    sync_bargainfox: "BargainFox",
};

interface BulkResult {
    added: number;
    updated: number;
    removed: number;
    errors: { row: number; reason: string }[];
}

// Edit-modal form state — strings throughout so the user can clear an
// input without it snapping to 0/null. Coerced back to numbers/nulls
// at save time.
interface EditDraft {
    sku: string;            // read-only mirror of the original SKU
    master_sku: string;
    title: string;
    barcode: string;
    image_url: string;
    weight_grams: string;
    length_cm: string;
    width_cm: string;
    height_cm: string;
    country_of_origin: string;
    commodity_code: string;
    supplier_name: string;
    ship_in_packaging: boolean;
    extra_protection: boolean;
    ships_as_pallet: boolean;
    soft_item: boolean;
    rrp: string;
    cost_price: string;
    sync_jobalots: boolean;
    sync_bargainfox: boolean;
    extra_barcodes: string[];
    status: "active" | "archived";
}

const draftFromProduct = (p: Product): EditDraft => ({
    sku: p.sku,
    master_sku: p.master_sku ?? "",
    title: p.title ?? "",
    barcode: p.barcode ?? "",
    image_url: p.image_url ?? "",
    weight_grams: p.weight_grams != null ? String(p.weight_grams) : "",
    length_cm: p.length_cm != null ? String(p.length_cm) : "",
    width_cm:  p.width_cm  != null ? String(p.width_cm)  : "",
    height_cm: p.height_cm != null ? String(p.height_cm) : "",
    country_of_origin: p.country_of_origin ?? "",
    commodity_code:    p.commodity_code    ?? "",
    supplier_name:     p.supplier_name     ?? "",
    ship_in_packaging: !!p.ship_in_packaging,
    extra_protection:  !!p.extra_protection,
    ships_as_pallet:   !!p.ships_as_pallet,
    soft_item:         !!p.soft_item,
    rrp:               p.rrp != null ? String(p.rrp) : "",
    cost_price:        p.cost_price != null ? String(p.cost_price) : "",
    sync_jobalots:     !!p.sync_jobalots,
    sync_bargainfox:   !!p.sync_bargainfox,
    extra_barcodes:    Array.isArray(p.extra_barcodes) ? [...p.extra_barcodes] : [],
    status: p.archived_at ? "archived" : "active",
});

// Column order used for XLSX download/template/upload. Mirrors the BE
// `fieldNames` array so a round-trip download → upload is lossless.
// `status` is appended last (active|archived) — read on upload to drive
// archive/restore via the BE update path without needing a separate
// remove row.
// `thumbnail_url` is deliberately absent — derived server-side from
// image_url. Admins never see or set it.
const COLUMNS = [
    "action",
    "sku",
    "master_sku",
    "title",
    "barcode",
    "extra_barcodes",
    "image_url",
    "weight_grams",
    "length_cm",
    "width_cm",
    "height_cm",
    "country_of_origin",
    "commodity_code",
    "supplier_name",
    "ship_in_packaging",
    "extra_protection",
    "ships_as_pallet",
    "soft_item",
    "rrp",
    "cost_price",
    "sync_jobalots",
    "sync_bargainfox",
    "status",
] as const;

// Identifier-style columns that must round-trip as plain text. Without an
// explicit Text numFmt Excel reinterprets long digit strings as numbers
// (barcode 5012345678901 → 5.01235E+12, lossy on save) and HS commodity
// codes like "9999.00.00" as dates. SKUs occasionally arrive as numeric
// barcodes from suppliers, hence included here.
const TEXT_COLUMNS = new Set<string>(["sku", "master_sku", "barcode", "extra_barcodes", "commodity_code"]);

const cellToString = (cell: ExcelJS.Cell | undefined): string => {
    if (!cell) return "";
    const v = cell.value;
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    if (typeof v === "object") {
        if ("result" in v && (v as { result?: unknown }).result != null) {
            return String((v as { result: unknown }).result);
        }
        if ("text" in v && (v as { text?: unknown }).text != null) {
            return String((v as { text: unknown }).text);
        }
        if ("richText" in v) {
            const rt = (v as { richText?: { text: string }[] }).richText;
            return Array.isArray(rt) ? rt.map(r => r.text).join("") : "";
        }
    }
    return String(v);
};

const downloadXlsx = async (filename: string, header: readonly string[], rows: string[][]) => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Products");
    ws.addRow(header as string[]);
    for (const r of rows) ws.addRow(r);
    header.forEach((name, i) => {
        if (TEXT_COLUMNS.has(name)) ws.getColumn(i + 1).numFmt = "@";
    });
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const readXlsxRows = async (file: File): Promise<{ header: string[]; rows: string[][] }> => {
    const buffer = await file.arrayBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.worksheets[0];
    if (!ws || ws.rowCount < 1) return { header: [], rows: [] };
    const headerRow = ws.getRow(1);
    const colCount = headerRow.cellCount;
    const header: string[] = [];
    for (let c = 1; c <= colCount; c++) {
        header[c - 1] = cellToString(headerRow.getCell(c)).trim().toLowerCase();
    }
    const rows: string[][] = [];
    for (let r = 2; r <= ws.rowCount; r++) {
        const xrow = ws.getRow(r);
        const cells: string[] = [];
        for (let c = 1; c <= colCount; c++) cells[c - 1] = cellToString(xrow.getCell(c));
        if (cells.some(s => s.trim() !== "")) rows.push(cells);
    }
    return { header, rows };
};

const CHUNK_SIZE = 200;

// Belt-and-braces guard for a hand-built XLSX where the operator hasn't
// formatted SKU/barcode as Text — Excel will store the value as a number
// and exceljs will hand us back a scientific-notation string once it's
// past 11 digits. Our own download formats those columns as Text, so a
// round-trip never trips this check.
const SCIENTIFIC_NOTATION_RE = /^-?\d+(\.\d+)?[eE][+-]?\d+$/;
const isScientificNotation = (value: string): boolean =>
    SCIENTIFIC_NOTATION_RE.test(value.trim());

// Logs tab — page size for the per-product history. Keeps the modal
// scope tight (no pagination UI inside the dialog) while still showing
// enough rows that the typical recent-edits use case is covered.
const LOGS_PAGE_SIZE = 50;

// Mirror of LogsAdmin.formatTs — dd/mm/yy hh:mm:ss in local time. Inlined
// so the modal isn't coupled to LogsAdmin's internals; the format is
// short enough that drift between the two is unlikely to matter.
const formatLogTs = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} `
         + `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// Pull a human-readable summary of what happened on this product out of
// the bulk request body the middleware stashed on `metadata.rows`. The
// indexed identifier columns (tote_id, location, order_no, etc.) are
// rarely populated on a products.bulk row, so without this the modal
// Logs tab renders an empty "—" for every entry.
//
// Format examples:
//   rename → 123TEST12               (single rename)
//   update                           (single field update)
//   rename → 123TEST12, update       (rename + follow-up update bundle)
//   remove                           (archive)
const summariseProductMetadata = (metadata: LogRow["metadata"]): string => {
    if (!metadata || typeof metadata !== "object") return "";
    const rows = (metadata as { rows?: unknown }).rows;
    if (!Array.isArray(rows)) return "";
    const parts: string[] = [];
    for (const row of rows as Array<Record<string, unknown>>) {
        const action = String(row?.action ?? "").trim().toLowerCase();
        if (!action) continue;
        if (action === "rename" && row?.new_sku) {
            parts.push(`rename → ${String(row.new_sku)}`);
        } else {
            parts.push(action);
        }
    }
    return parts.join(", ");
};

// Tighter version of LogsAdmin.renderContextChips — the SKU chip is
// dropped because the whole tab is already filtered to one product, so
// repeating it on every row is just noise. An "Action" chip is added
// from the bulk metadata so the Context column actually reads as
// something for the typical products.bulk row whose indexed columns are
// otherwise empty.
const renderProductLogContext = (r: LogRow): ReactNode => {
    const chips: { label: string; value: string }[] = [];
    if (r.tote_id)  chips.push({ label: "Tote",   value: r.tote_id });
    if (r.location) chips.push({ label: "Loc",    value: r.location });
    if (r.order_no) chips.push({ label: "Order",  value: r.order_no });
    if (r.po)       chips.push({ label: "PO",     value: r.po });
    if (r.barcode && r.barcode !== r.sku) chips.push({ label: "Barcode", value: r.barcode });
    if (r.item)     chips.push({ label: "Item",   value: r.item });
    const actionSummary = summariseProductMetadata(r.metadata);
    if (actionSummary) chips.push({ label: "Action", value: actionSummary });
    if (chips.length === 0) return <span className="logs-context-empty">—</span>;
    return (
        <span className="logs-context-chips">
            {chips.map(c => (
                <span key={c.label} className="logs-context-chip">
                    <span className="logs-context-chip-label">{c.label}:</span>
                    <span className="logs-context-chip-value">{c.value}</span>
                </span>
            ))}
        </span>
    );
};

const ProductsAdmin = () => {
    const { t } = useTranslation();
    // Access-control read-only mode (ACCESS_CONTROL_PLAN.md §8.4). Edit
    // (row click), bulk upload and push-stock are the mutation entry points.
    const readOnly = useReadOnly();
    // Full product list — needed for the table view and the download.
    // Was previously lazy-loaded only on download, but the table view
    // wants it on mount. Counts are derived from the same array.
    const [products, setProducts] = useState<Product[]>([]);
    const [total, setTotal] = useState(0);
    const [suppliersList, setSuppliersList] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    // Supplier preflight modal — populated when an XLSX upload references
    // a supplier that isn't active in the suppliers table. Holds a resolver
    // promise so handleUpload can `await` the user's choice.
    const [missingSuppliers, setMissingSuppliers] = useState<{
        missing: string[];
        archived: string[];
        resolve: (confirmed: boolean) => void;
    } | null>(null);
    const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
    const [lastResult, setLastResult] = useState<BulkResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filters + pagination — same UX shape as LocationsAdmin /
    // TotesAdmin so admins moving between admin pages see one
    // consistent list interface. Search commits on Enter / blur so a
    // fast typist doesn't fire a request per keystroke. `?search=`
    // URL param lets AdminSearch deep-link in pre-filtered.
    const [search, setSearch] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(search);
    useEffect(() => { setSearchInput(search); }, [search]);
    const [statusFilter, setStatusFilter] = useState("");  // "" | "active" | "archived"
    const [supplierFilter, setSupplierFilter] = useState("");
    const [page, setPage] = useState(0);

    // Edit modal — opens when the user clicks a row. `editing` holds the
    // pristine snapshot so we can reset on cancel; `draft` mirrors it as
    // editable form state.
    const [editing, setEditing] = useState<Product | null>(null);
    const [draft, setDraft] = useState<EditDraft | null>(null);
    const [savingEdit, setSavingEdit] = useState(false);
    const [pushingStock, setPushingStock] = useState(false);
    // Current on-hand for the edited SKU. Refreshed when the modal
    // opens and after a successful Push. null = not loaded yet.
    const [inventorySummary, setInventorySummary] = useState<{
        total_available: number;
        warehouses: { warehouse: string; available: number }[];
    } | null>(null);
    // Latest /stock/sync status per (channel, vendor). Refreshed
    // when the modal opens and on a poll cycle after the Push button.
    type StockSyncRow = {
        channel_code: string;
        channel_name: string;
        vendor_key: string;
        vendor_name: string | null;
        warehouse: string;
        available_quantity: number;
        status: "pending" | "delivered" | "abandoned";
        status_code: number | null;
        error: string | null;
        queued_at: string;
        delivered_at: string | null;
        duration_ms: number | null;
        request_body: unknown;
        response_body: unknown;
    };
    const [syncStatus, setSyncStatus] = useState<StockSyncRow[] | null>(null);
    const [pollingSync, setPollingSync] = useState(false);
    // Which (channel+vendor) row's API trace is currently expanded.
    // Single string so opening row B closes row A — keeps the UI tidy.
    const [expandedSyncRow, setExpandedSyncRow] = useState<string | null>(null);
    // Modal tab + per-product log history. Logs are lazy-loaded when the
    // operator first switches to the Logs tab, then cached for the
    // lifetime of the modal — re-opening the modal re-fetches.
    const [modalTab, setModalTab] = useState<"details" | "logs">("details");
    const [logsLoading, setLogsLoading] = useState(false);
    const [logs, setLogs] = useState<LogRow[]>([]);
    // Click-to-expand: clicking a log row toggles a metadata details row
    // beneath it. Only one row open at a time so the table doesn't
    // grow unboundedly tall on a busy product.
    const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

    useEffect(() => { setPage(0); }, [search, statusFilter, supplierFilter]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            // status maps directly: "" → all, "active" → active only,
            // "archived" → archived only. Lets the BE skip work and
            // return correct totals per filter.
            params.set("status", statusFilter || "all");
            if (supplierFilter)     params.set("supplier_name", supplierFilter);
            if (search.trim())      params.set("q", search.trim());
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/products?${params}`, {});
            if (!res.ok) throw new Error("load");
            const data = await res.json();
            setProducts(Array.isArray(data.products) ? data.products : []);
            setTotal(typeof data.total === "number" ? data.total : 0);
        } catch {
            toast.error(t("server_error"), { toastId: "products-load-err" });
        } finally {
            setLoading(false);
        }
    }, [statusFilter, supplierFilter, search, page, t]);

    useEffect(() => { refresh(); }, [refresh]);

    // Pull active suppliers from the canonical /suppliers table. Feeds
    // both the page-level supplier filter pill and the modal's supplier
    // dropdown. Previously this scraped distinct supplier_name out of a
    // page_size=500 products fetch; switching to /suppliers means an
    // archived supplier no longer disappears the moment its last product
    // is renamed and a freshly-added supplier shows up immediately.
    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(
                    `/suppliers?status=active&page=0&page_size=500`,
                    {},
                );
                if (!res.ok) return;
                const data = await res.json();
                const names: string[] = Array.isArray(data?.suppliers)
                    ? data.suppliers.map((s: { name?: string }) => s.name).filter((n: unknown): n is string => typeof n === "string" && n !== "")
                    : [];
                setSuppliersList(names.sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    const fetchInventorySummary = async (sku: string) => {
        try {
            const res = await apiFetch(`/products/${encodeURIComponent(sku)}/inventory-summary`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setInventorySummary({
                total_available: Number(data.total_available) || 0,
                warehouses: Array.isArray(data.warehouses) ? data.warehouses : [],
            });
        } catch {
            setInventorySummary({ total_available: 0, warehouses: [] });
        }
    };

    const fetchSyncStatus = async (sku: string) => {
        try {
            const res = await apiFetch(`/products/${encodeURIComponent(sku)}/stock-sync-recent`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setSyncStatus(Array.isArray(data.rows) ? data.rows : []);
        } catch {
            // Leave previous status visible on transient errors.
        }
    };

    // Poll sync status every 2.5s while any row is still pending.
    // Stops automatically once everything's settled (delivered or
    // abandoned). The poll itself is cheap — one indexed query.
    useEffect(() => {
        if (!editing || !pollingSync) return;
        const anyPending = (syncStatus ?? []).some((r) => r.status === "pending");
        if (!anyPending && syncStatus !== null) {
            // Settled — one last refresh and stop.
            setPollingSync(false);
            return;
        }
        const id = setInterval(() => { void fetchSyncStatus(editing.sku); }, 2500);
        return () => clearInterval(id);
    }, [editing, pollingSync, syncStatus]);

    const openEdit = (p: Product) => {
        if (readOnly) return;
        setEditing(p);
        setDraft(draftFromProduct(p));
        setModalTab("details");
        setLogs([]);
        setInventorySummary(null);
        setSyncStatus(null);
        setPollingSync(false);
        // Fire-and-forget — the panels render a placeholder while loading.
        if (p.sync_jobalots || p.sync_bargainfox) {
            fetchInventorySummary(p.sku);
            fetchSyncStatus(p.sku);
        }
    };
    const closeEdit = () => {
        if (savingEdit) return;
        setEditing(null);
        setDraft(null);
        setLogs([]);
        setInventorySummary(null);
        setSyncStatus(null);
        setPollingSync(false);
    };

    // Fetch this product's history when the operator opens the Logs tab.
    // BE matches `sku` case-sensitively (LIKE, not ILIKE) so the SKU has
    // to be passed verbatim — see Portal-BE/app/admin/log.domain.js
    // SEARCH_FIELDS_CS. Lazy-loaded because the typical edit doesn't open
    // the tab at all.
    useEffect(() => {
        if (!editing || modalTab !== "logs") return;
        let cancelled = false;
        const sku = editing.sku;
        setLogsLoading(true);
        (async () => {
            try {
                const params = new URLSearchParams();
                params.set("q", sku);
                params.set("search_field", "sku");
                params.set("page", "0");
                params.set("page_size", String(LOGS_PAGE_SIZE));
                const res = await apiFetch(`/admin/logs?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setLogs(Array.isArray(data?.rows) ? data.rows : []);
            } catch {
                if (!cancelled) {
                    setLogs([]);
                    toast.error(t("server_error"), { toastId: `prod-logs-${sku}` });
                }
            } finally {
                if (!cancelled) setLogsLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [editing, modalTab, t]);
    const setField = <K extends keyof EditDraft>(key: K, value: EditDraft[K]) => {
        setDraft(prev => prev ? { ...prev, [key]: value } : prev);
    };

    const addExtraBarcode = () => {
        setDraft(prev => prev ? { ...prev, extra_barcodes: [...prev.extra_barcodes, ""] } : prev);
    };
    const updateExtraBarcode = (idx: number, value: string) => {
        setDraft(prev => {
            if (!prev) return prev;
            const next = prev.extra_barcodes.slice();
            next[idx] = value;
            return { ...prev, extra_barcodes: next };
        });
    };
    const removeExtraBarcode = (idx: number) => {
        setDraft(prev => {
            if (!prev) return prev;
            const next = prev.extra_barcodes.slice();
            next.splice(idx, 1);
            return { ...prev, extra_barcodes: next };
        });
    };

    // Save flow — combines up to three /products/bulk operations in
    // ONE request so the BE handles them atomically (rename → field
    // update → archive). Cases handled:
    //   • SKU edited       → "rename"  runs first; subsequent rows use
    //                        the new SKU as the lookup key.
    //   • Status archived  → "remove"  flips archived_at = NOW().
    //   • Active path      → "update"  applies all fields; the BE clears
    //                        archived_at on every update so this also
    //                        restores a row that was previously archived.
    // The BE applies rows in order, so `rename` first means the later
    // `update`/`remove` find the newly-named row.
    const saveEdit = async () => {
        if (!editing || !draft) return;
        setSavingEdit(true);
        try {
            const oldSku = editing.sku;
            const newSku = draft.sku.trim() || oldSku;
            const skuChanged = newSku !== oldSku;
            const wasArchived = !!editing.archived_at;
            const nowArchived = draft.status === "archived";
            const rows: Record<string, unknown>[] = [];

            if (skuChanged) {
                rows.push({ action: "rename", sku: oldSku, new_sku: newSku });
            }

            if (nowArchived && !wasArchived) {
                // Archive (and possibly rename) — drop field edits since
                // the row's about to be hidden anyway.
                rows.push({ action: "remove", sku: newSku });
            } else if (nowArchived && wasArchived) {
                // Already archived; only act if the SKU was renamed,
                // otherwise close silently.
                if (rows.length === 0) {
                    closeEdit();
                    setSavingEdit(false);
                    return;
                }
            } else {
                // Active path: send all fields. Empty strings → null on
                // the BE so blank inputs clear the column rather than
                // saving the literal "".
                rows.push({
                    action: "update",
                    sku: newSku,
                    master_sku: draft.master_sku.trim(),
                    title: draft.title.trim(),
                    barcode: draft.barcode.trim(),
                    extra_barcodes: draft.extra_barcodes
                        .map(b => b.trim())
                        .filter(b => b.length > 0)
                        .join(";"),
                    image_url: draft.image_url.trim(),
                    weight_grams: draft.weight_grams.trim(),
                    length_cm: draft.length_cm.trim(),
                    width_cm:  draft.width_cm.trim(),
                    height_cm: draft.height_cm.trim(),
                    country_of_origin: draft.country_of_origin.trim(),
                    commodity_code:    draft.commodity_code.trim(),
                    supplier_name:     draft.supplier_name.trim(),
                    ship_in_packaging: draft.ship_in_packaging ? "true" : "false",
                    extra_protection:  draft.extra_protection ? "true" : "false",
                    ships_as_pallet:   draft.ships_as_pallet ? "true" : "false",
                    soft_item:         draft.soft_item ? "true" : "false",
                    rrp:               draft.rrp.trim(),
                    cost_price:        draft.cost_price.trim(),
                    sync_jobalots:     draft.sync_jobalots ? "true" : "false",
                    sync_bargainfox:   draft.sync_bargainfox ? "true" : "false",
                });
            }

            const res = await apiFetch(`/products/bulk`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rows }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const result: BulkResult = await res.json();
            if (result.errors.length > 0) {
                toast.error(result.errors[0].reason, { toastId: `prod-edit-${editing.sku}` });
                return;
            }
            await refresh();
            toast.success(t("saved"), { toastId: `prod-edit-ok-${editing.sku}` });
            setEditing(null);
            setDraft(null);
        } catch {
            toast.error(t("server_error"), { toastId: `prod-edit-err-${editing.sku}` });
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDownloadTemplate = async () => {
        // Row layout: action, sku, master_sku, title, barcode,
        // extra_barcodes, image_url, weight_grams, length_cm, width_cm,
        // height_cm, country_of_origin, commodity_code, supplier_name,
        // ship_in_packaging, extra_protection, ships_as_pallet, soft_item,
        // rrp, cost_price, sync_jobalots, sync_bargainfox, status — one
        // value per COLUMNS entry, in that order. extra_barcodes is a
        // ';'-delimited list of secondary barcodes that should also
        // resolve to this SKU on a scan. cost_price is normally inherited
        // from the latest manifest but can be set / overridden here.
        const blanks = (n: number) => Array<string>(n).fill("");
        const example: string[][] = [
            ["add", "JLI-EX-001", "JLI-EX-001-MASTER", "Example Product", "5012345678901", "5012345678902;5012345678903", "https://example.com/img.jpg", "500", "20", "15", "10", "GB", "9999.00.00", "Acme Ltd", "false", "false", "false", "false", "29.99", "12.50", "true",  "false", "active"],
            ["update", "JLI-EX-001", "", "Example Product (renamed)", ...blanks(COLUMNS.length - 5), "active"],
            ["update", "JLI-EX-001", ...blanks(COLUMNS.length - 3), "archived"],
            ["remove", "JLI-EX-001", ...blanks(COLUMNS.length - 2)],
        ];
        await downloadXlsx("products-template.xlsx", COLUMNS, example);
    };

    const handleDownloadCurrent = async () => {
        // Page through the BE so the download covers every product matching
        // the current filters, not just the visible 25-row page slice.
        // BE caps page_size at 500 (products controller listProducts).
        const DOWNLOAD_PAGE_SIZE = 500;
        if (total > DOWNLOAD_MAX_ROWS) {
            toast.error(t("products_download_limit", { max: DOWNLOAD_MAX_ROWS.toLocaleString(), total: total.toLocaleString() }), { toastId: "products-download-limit" });
            return;
        }
        const all: Product[] = [];
        try {
            const baseParams = new URLSearchParams();
            baseParams.set("status", statusFilter || "all");
            if (supplierFilter) baseParams.set("supplier_name", supplierFilter);
            if (search.trim())  baseParams.set("q", search.trim());
            baseParams.set("page_size", String(DOWNLOAD_PAGE_SIZE));

            let pageNum = 0;
            while (true) {
                const params = new URLSearchParams(baseParams);
                params.set("page", String(pageNum));
                const res = await apiFetch(`/products?${params}`, {});
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                const list: Product[] = Array.isArray(data.products) ? data.products : [];
                all.push(...list);
                const grandTotal = typeof data.total === "number" ? data.total : all.length;
                if (list.length === 0 || all.length >= grandTotal) break;
                pageNum += 1;
            }
        } catch {
            toast.error(t("server_error"), { toastId: "products-download-err" });
            return;
        }

        const rows: string[][] = [];
        for (const p of all) {
            rows.push([
                "update",
                p.sku,
                p.master_sku ?? "",
                p.title ?? "",
                p.barcode ?? "",
                Array.isArray(p.extra_barcodes) ? p.extra_barcodes.join(";") : "",
                p.image_url ?? "",
                p.weight_grams != null ? String(p.weight_grams) : "",
                p.length_cm != null ? String(p.length_cm) : "",
                p.width_cm != null ? String(p.width_cm) : "",
                p.height_cm != null ? String(p.height_cm) : "",
                p.country_of_origin ?? "",
                p.commodity_code ?? "",
                p.supplier_name ?? "",
                p.ship_in_packaging ? "true" : "false",
                p.extra_protection ? "true" : "false",
                p.ships_as_pallet ? "true" : "false",
                p.soft_item ? "true" : "false",
                p.rrp != null ? String(p.rrp) : "",
                p.cost_price != null ? String(p.cost_price) : "",
                p.sync_jobalots ? "true" : "false",
                p.sync_bargainfox ? "true" : "false",
                p.archived_at ? "archived" : "active",
            ]);
        }
        await downloadXlsx("products.xlsx", COLUMNS, rows);
    };

    const handleUpload = async (file: File) => {
        let parsed: { header: string[]; rows: string[][] };
        try {
            parsed = await readXlsxRows(file);
        } catch {
            toast.error(t("products_upload_parse_error"), { toastId: "products-upload-parse" });
            return;
        }
        const { header, rows } = parsed;
        if (rows.length === 0) {
            toast.error(t("products_upload_empty"), { toastId: "products-upload-empty" });
            return;
        }
        const requiredHeaders = ["action", "sku"];
        for (const req of requiredHeaders) {
            if (!header.includes(req)) {
                toast.error(t("products_upload_bad_header"), { toastId: "products-bad-header" });
                return;
            }
        }
        const objRows = rows.map(cells => {
            const obj: Record<string, string> = {};
            header.forEach((h, i) => { obj[h] = cells[i] ?? ""; });
            return obj;
        });

        // Supplier preflight: every supplier_name in the file must be an
        // active supplier in the suppliers table. If any are missing or
        // archived, prompt the operator. On cancel, bail before writing
        // a single row. On confirm, create / restore the suppliers and
        // continue with the upload.
        try {
            const preflight = await preflightSuppliers(objRows);
            if (!preflight.ok) {
                const confirmed = await new Promise<boolean>(resolve => {
                    setMissingSuppliers({ ...preflight, resolve });
                });
                setMissingSuppliers(null);
                if (!confirmed) {
                    toast.info(t("products_upload_cancelled_missing_suppliers"), {
                        toastId: "products-upload-cancelled",
                    });
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    return;
                }
                const fixupRows = buildSupplierFixupRows(preflight.missing, preflight.archived);
                if (fixupRows.length > 0) {
                    const r = await apiFetch(`/suppliers/bulk`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ rows: fixupRows }),
                    });
                    if (!r.ok) throw new Error(`suppliers fixup failed: ${r.status}`);
                }
            }
        } catch {
            toast.error(t("server_error"), { toastId: "products-preflight-err" });
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        // Pre-flight: catch Excel scientific-notation corruption before
        // it hits the BE. A round-trip through our own download can't
        // produce these (Text-typed columns), but a hand-built XLSX
        // without that formatting can — we drop the row and report it
        // rather than store a lossy value.
        setUploading(true);
        setLastResult(null);
        const summary: BulkResult = { added: 0, updated: 0, removed: 0, errors: [] };
        const safeRows: typeof objRows = [];
        for (let i = 0; i < objRows.length; i++) {
            const row = objRows[i];
            const rowNum = i + 2; // +1 zero-index, +1 header
            const sku = (row.sku ?? "").trim();
            const barcode = (row.barcode ?? "").trim();
            if (sku && isScientificNotation(sku)) {
                summary.errors.push({ row: rowNum, reason: t("products_upload_scientific_notation", { field: "sku", value: sku }) });
                continue;
            }
            if (barcode && isScientificNotation(barcode)) {
                summary.errors.push({ row: rowNum, reason: t("products_upload_scientific_notation", { field: "barcode", value: barcode }) });
                continue;
            }
            safeRows.push(row);
        }

        const total = safeRows.length;
        try {
            for (let i = 0; i < total; i += CHUNK_SIZE) {
                const chunk = safeRows.slice(i, i + CHUNK_SIZE);
                setProgress({ done: i, total });
                const res = await apiFetch(`/products/bulk`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ rows: chunk }),
                });
                // 413 = chunk body exceeds the BE's JSON limit. Stop the
                // upload (subsequent chunks will fail the same way) and
                // surface a specific message so the operator knows it's
                // a size issue.
                if (res.status === 413) {
                    toast.error(t("bulk_upload_too_large"), { toastId: "products-upload-413" });
                    break;
                }
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const r: BulkResult = await res.json();
                summary.added += r.added;
                summary.updated += r.updated;
                summary.removed += r.removed;
                for (const e of r.errors) {
                    summary.errors.push({ row: e.row + i, reason: e.reason });
                }
            }
            setProgress({ done: total, total });
            setLastResult(summary);
            await refresh();
            toast.success(
                t("totes_bulk_summary", {
                    added: String(summary.added),
                    updated: String(summary.updated),
                    removed: String(summary.removed),
                }),
                { toastId: "products-upload-ok" },
            );
        } catch {
            toast.error(t("server_error"), { toastId: "products-upload-err" });
        } finally {
            setUploading(false);
            setTimeout(() => setProgress(null), 1500);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    // BE returns the page slice already filtered + searched. The supplier
    // dropdown is fed by suppliersList (loaded separately on mount) so
    // filter options span the whole catalogue, not just the current page.
    const visible = products;
    const suppliers = suppliersList;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    if (loading) return <div className="admin-settings-loading">{t("loading")}</div>;

    return (
        <div className="products-admin">
            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={handleDownloadTemplate}>
                    <Download size={14} />
                    {t("totes_download_template")}
                </button>
                <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={handleDownloadCurrent}
                    disabled={products.length === 0 || total > DOWNLOAD_MAX_ROWS}
                    title={total > DOWNLOAD_MAX_ROWS
                        ? t("products_download_limit", { max: DOWNLOAD_MAX_ROWS.toLocaleString(), total: total.toLocaleString() })
                        : undefined}
                >
                    <Download size={14} />
                    {t("totes_download_current")}
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    <Upload size={14} />
                    {uploading
                        ? (progress ? t("totes_upload_progress", { done: String(progress.done), total: String(progress.total) }) : t("submitting"))
                        : t("products_upload_xlsx")}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: "none" }}
                    disabled={readOnly}
                    onChange={e => {
                        const f = e.target.files?.[0];
                        if (f && !readOnly) handleUpload(f);
                    }}
                />
            </div>

            {lastResult && (
                <div className="totes-admin-result">
                    <CheckCircle strokeWidth={3} size={16} className="totes-admin-result-icon" />
                    <span>
                        {t("totes_bulk_summary", {
                            added: String(lastResult.added),
                            updated: String(lastResult.updated),
                            removed: String(lastResult.removed),
                        })}
                    </span>
                    {lastResult.errors.length > 0 && (
                        <details className="totes-admin-errors">
                            <summary>
                                <AlertCircle size={14} />
                                {lastResult.errors.length} {lastResult.errors.length === 1 ? "error" : "errors"}
                            </summary>
                            <ul>
                                {lastResult.errors.slice(0, 50).map((e, i) => (
                                    <li key={i}>
                                        {t("totes_row_label", { row: String(e.row) })}: {e.reason}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}

            {/* Compact summary strip — total reflects the current filter
                (BE side). Per-status splits dropped because the BE only
                returns the filtered page; per-status counts would need a
                second round trip and weren't load-bearing. */}
            <div className="locations-summary">
                <div className="locations-summary-total">
                    <span className="locations-summary-total-label">{t("products_total")}</span>
                    <strong>{total}</strong>
                </div>
            </div>

            <FilterPills
                className="warehouse-admin-pills"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                    { value: "active",   label: t("products_active") },
                    { value: "archived", label: t("products_archived") },
                ]}
            />

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("products_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            setSearch(searchInput.trim());
                        }
                    }}
                    onBlur={() => {
                        if (searchInput.trim() !== search.trim()) {
                            setSearch(searchInput.trim());
                        }
                    }}
                />
                <select
                    className="locations-table-search wh-admin-filter-select"
                    value={supplierFilter}
                    onChange={e => setSupplierFilter(e.target.value)}
                >
                    <option value="">{t("products_filter_all_suppliers")}</option>
                    {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>

            {total === 0 ? (
                <div className="locations-table-empty">{t("products_no_matches")}</div>
            ) : (
                <>
                    <div className="locations-table-wrap products-admin-table-wrap">
                        <table className="locations-table products-admin-table">
                            <thead>
                                <tr>
                                    {/* Inline style + table-layout:fixed on the
                                        <table> drives deterministic column
                                        widths so long unbreakable values (a
                                        13-digit barcode used as a SKU renders
                                        as `1.92333E+11` in mono font) can't
                                        push the layout out of alignment. */}
                                    <th style={{ width: 170 }}>{t("products_col_sku")}</th>
                                    <th>{t("products_col_title")}</th>
                                    <th style={{ width: 170 }}>{t("products_col_barcode")}</th>
                                    <th className="locations-table-num" style={{ width: 90 }}>{t("products_col_weight")}</th>
                                    <th className="locations-table-num" style={{ width: 90 }}>{t("products_col_rrp")}</th>
                                    <th className="locations-table-num" style={{ width: 90 }}>{t("products_col_cost_price")}</th>
                                    <th style={{ width: 180 }}>{t("products_col_supplier")}</th>
                                    <th style={{ width: 110 }}>{t("products_col_status")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(p => (
                                    <tr
                                        key={p.sku}
                                        className="products-admin-row"
                                        onClick={() => openEdit(p)}
                                    >
                                        <td className="locations-table-mono products-admin-cell-truncate" title={p.sku}>{p.sku}</td>
                                        <td className="products-admin-cell-truncate" title={p.title || ""}>{p.title || "—"}</td>
                                        <td className="locations-table-mono products-admin-cell-truncate" title={p.barcode || ""}>{p.barcode || "—"}</td>
                                        <td className="locations-table-num">
                                            {p.weight_grams != null ? `${p.weight_grams} g` : "—"}
                                        </td>
                                        <td className="locations-table-num">
                                            {p.rrp != null && p.rrp !== "" ? `£${Number(p.rrp).toFixed(2)}` : "—"}
                                        </td>
                                        <td className="locations-table-num">
                                            {p.cost_price != null && p.cost_price !== "" ? `£${Number(p.cost_price).toFixed(2)}` : "—"}
                                        </td>
                                        <td className="products-admin-cell-truncate" title={p.supplier_name || ""}>{p.supplier_name || "—"}</td>
                                        <td>
                                            <span className={`products-admin-status${p.archived_at ? " products-admin-status--archived" : ""}`}>
                                                {p.archived_at ? t("products_archived") : t("products_active")}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="locations-table-footer">
                        <span>{t("locations_showing_range", {
                            from: String(rangeFrom),
                            to: String(rangeTo),
                            total: String(total),
                        })}</span>
                        <div className="locations-table-pagination">
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.max(0, p - 1))}
                                disabled={safePage === 0}
                                aria-label={t("pagination_previous")}
                            >
                                <ChevronLeft size={16} />
                                <span>{t("pagination_previous")}</span>
                            </button>
                            <span className="locations-table-page-indicator">
                                {t("pagination_page_x_of_y", {
                                    page: String(safePage + 1),
                                    total: String(totalPages),
                                })}
                            </span>
                            <button
                                type="button"
                                className="locations-table-page-btn"
                                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                disabled={safePage >= totalPages - 1}
                                aria-label={t("pagination_next")}
                            >
                                <span>{t("pagination_next")}</span>
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {missingSuppliers && (
                <MissingSuppliersModal
                    missing={missingSuppliers.missing}
                    archived={missingSuppliers.archived}
                    onConfirm={() => missingSuppliers.resolve(true)}
                    onCancel={() => missingSuppliers.resolve(false)}
                />
            )}

            {editing && draft && (
                <div className="modal-overlay" onClick={closeEdit}>
                    <div
                        className="modal-card products-admin-modal"
                        onClick={e => e.stopPropagation()}
                        role="dialog"
                        aria-labelledby="products-edit-title"
                    >
                        <div className="modal-card-header products-admin-modal-header">
                            <div className="products-admin-modal-titles">
                                <h2 id="products-edit-title" className="products-admin-modal-title">
                                    {t("products_modal_title")}
                                </h2>
                                <span className="products-admin-modal-sku" title={editing.sku}>
                                    {editing.sku}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={closeEdit}
                                disabled={savingEdit}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="products-admin-modal-tabs" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={modalTab === "details"}
                                className={`products-admin-modal-tab${modalTab === "details" ? " products-admin-modal-tab--active" : ""}`}
                                onClick={() => setModalTab("details")}
                            >
                                {t("products_modal_tab_details")}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={modalTab === "logs"}
                                className={`products-admin-modal-tab${modalTab === "logs" ? " products-admin-modal-tab--active" : ""}`}
                                onClick={() => setModalTab("logs")}
                            >
                                {t("products_modal_tab_logs")}
                            </button>
                        </div>
                        <div className="modal-card-body products-admin-modal-body">
                            {modalTab === "details" ? (
                            <div className="products-admin-form-sections">
                                {/* Identity — what this product IS. SKU pair + the
                                    two dropdowns that classify it (supplier + status). */}
                                <section className="products-admin-form-section products-admin-form-section--identity">
                                    <h3 className="products-admin-form-section-title">{t("products_modal_section_identity")}</h3>
                                    <div className="products-admin-form-section-grid">
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_sku")}
                                            <input
                                                className="dispatch-admin-form-input"
                                                value={draft.sku}
                                                onChange={e => setField("sku", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_master_sku")}
                                            <input
                                                className="dispatch-admin-form-input"
                                                value={draft.master_sku}
                                                onChange={e => setField("master_sku", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_supplier")}
                                            <select
                                                className="dispatch-admin-form-input"
                                                value={draft.supplier_name}
                                                onChange={e => setField("supplier_name", e.target.value)}
                                                disabled={savingEdit}
                                            >
                                                <option value="">—</option>
                                                {/* Sticky option for an archived supplier still
                                                    referenced by this product, so saving doesn't
                                                    silently rename it away. */}
                                                {draft.supplier_name && !suppliersList.includes(draft.supplier_name) && (
                                                    <option value={draft.supplier_name}>{draft.supplier_name}</option>
                                                )}
                                                {suppliersList.map(s => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_status")}
                                            <select
                                                className="dispatch-admin-form-input"
                                                value={draft.status}
                                                onChange={e => setField("status", e.target.value as "active" | "archived")}
                                                disabled={savingEdit}
                                            >
                                                <option value="active">{t("products_active")}</option>
                                                <option value="archived">{t("products_archived")}</option>
                                            </select>
                                        </label>
                                    </div>
                                </section>

                                {/* Listing — what the customer sees: title, image,
                                    scannable barcodes. Title and Image URL stretch
                                    full-width because the values typically overrun a
                                    quarter-column; Barcode block is half-width to
                                    leave the row breathing space. */}
                                <section className="products-admin-form-section products-admin-form-section--listing">
                                    <h3 className="products-admin-form-section-title">{t("products_modal_section_listing")}</h3>
                                    <div className="products-admin-form-section-grid">
                                        <label className="dispatch-admin-form-label products-admin-form-label--full">
                                            {t("products_col_title")}
                                            <input
                                                className="dispatch-admin-form-input"
                                                value={draft.title}
                                                onChange={e => setField("title", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label products-admin-form-label--full">
                                            {t("products_modal_image")}
                                            <input
                                                type="url"
                                                className="dispatch-admin-form-input"
                                                value={draft.image_url}
                                                onChange={e => setField("image_url", e.target.value)}
                                                placeholder="https://…"
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <div className="dispatch-admin-form-label products-admin-form-label--half">
                                            <span>{t("products_col_barcode")}</span>
                                            <div className="products-admin-barcode-row">
                                                <input
                                                    className="dispatch-admin-form-input"
                                                    value={draft.barcode}
                                                    onChange={e => setField("barcode", e.target.value)}
                                                    disabled={savingEdit}
                                                />
                                                <button
                                                    type="button"
                                                    className="products-admin-barcode-btn"
                                                    onClick={addExtraBarcode}
                                                    disabled={savingEdit}
                                                    aria-label={t("products_modal_add_barcode")}
                                                    title={t("products_modal_add_barcode")}
                                                >
                                                    <Plus size={16} />
                                                </button>
                                            </div>
                                            {draft.extra_barcodes.map((b, idx) => (
                                                <div key={idx} className="products-admin-barcode-row">
                                                    <input
                                                        className="dispatch-admin-form-input"
                                                        value={b}
                                                        onChange={e => updateExtraBarcode(idx, e.target.value)}
                                                        disabled={savingEdit}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="products-admin-barcode-btn"
                                                        onClick={() => removeExtraBarcode(idx)}
                                                        disabled={savingEdit}
                                                        aria-label={t("products_modal_remove_barcode")}
                                                        title={t("products_modal_remove_barcode")}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </section>

                                {/* Physical — everything dispatch needs to pack the
                                    box: weight, three dimensions, and whether it
                                    ships in its retail packaging. Four numeric
                                    fields fit one row exactly. */}
                                <section className="products-admin-form-section products-admin-form-section--physical">
                                    <h3 className="products-admin-form-section-title">{t("products_modal_section_physical")}</h3>
                                    <div className="products-admin-form-section-grid">
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_weight")} (g)
                                            <input
                                                type="number"
                                                inputMode="numeric"
                                                min={0}
                                                className="dispatch-admin-form-input"
                                                value={draft.weight_grams}
                                                onChange={e => setField("weight_grams", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_modal_length")}
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min={0}
                                                step={0.1}
                                                className="dispatch-admin-form-input"
                                                value={draft.length_cm}
                                                onChange={e => setField("length_cm", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_modal_width")}
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min={0}
                                                step={0.1}
                                                className="dispatch-admin-form-input"
                                                value={draft.width_cm}
                                                onChange={e => setField("width_cm", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_modal_height")}
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min={0}
                                                step={0.1}
                                                className="dispatch-admin-form-input"
                                                value={draft.height_cm}
                                                onChange={e => setField("height_cm", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label products-admin-form-label--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={draft.ship_in_packaging}
                                                onChange={e => setField("ship_in_packaging", e.target.checked)}
                                                disabled={savingEdit}
                                            />
                                            <span>{t("products_modal_ship_in_packaging")}</span>
                                        </label>
                                        <label className="dispatch-admin-form-label products-admin-form-label--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={draft.extra_protection}
                                                onChange={e => setField("extra_protection", e.target.checked)}
                                                disabled={savingEdit}
                                            />
                                            <span>{t("products_modal_extra_protection")}</span>
                                        </label>
                                        <label className="dispatch-admin-form-label products-admin-form-label--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={draft.ships_as_pallet}
                                                onChange={e => setField("ships_as_pallet", e.target.checked)}
                                                disabled={savingEdit}
                                            />
                                            <span>{t("products_modal_ships_as_pallet")}</span>
                                        </label>
                                        <label className="dispatch-admin-form-label products-admin-form-label--checkbox">
                                            <input
                                                type="checkbox"
                                                checked={draft.soft_item}
                                                onChange={e => setField("soft_item", e.target.checked)}
                                                disabled={savingEdit}
                                            />
                                            <span>{t("products_modal_soft_item")}</span>
                                        </label>
                                    </div>
                                </section>

                                {/* Pricing & customs — RRP for sales channels, plus
                                    customs paperwork (origin + commodity code) which
                                    sit alongside since they're both "what the
                                    product costs / where it came from" facts. */}
                                <section className="products-admin-form-section products-admin-form-section--commercial">
                                    <h3 className="products-admin-form-section-title">{t("products_modal_section_commercial")}</h3>
                                    <div className="products-admin-form-section-grid">
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_rrp")}
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min={0}
                                                step={0.01}
                                                className="dispatch-admin-form-input"
                                                value={draft.rrp}
                                                onChange={e => setField("rrp", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_cost_price")}
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min={0}
                                                step={0.01}
                                                className="dispatch-admin-form-input"
                                                value={draft.cost_price}
                                                onChange={e => setField("cost_price", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_col_origin")}
                                            <input
                                                className="dispatch-admin-form-input"
                                                value={draft.country_of_origin}
                                                onChange={e => setField("country_of_origin", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                        <label className="dispatch-admin-form-label">
                                            {t("products_modal_commodity")}
                                            <input
                                                className="dispatch-admin-form-input"
                                                value={draft.commodity_code}
                                                onChange={e => setField("commodity_code", e.target.value)}
                                                disabled={savingEdit}
                                            />
                                        </label>
                                    </div>
                                </section>

                                {/* Sales-channel toggles. Default off everywhere — sync
                                    only picks up SKUs where the operator has explicitly
                                    opted into a channel. Each entry maps to a NOT NULL
                                    BOOLEAN column on products; the BE sanitiser maps
                                    blank → false on insert. Adding another channel is
                                    one entry in CHANNEL_KEYS + a column. */}
                                <section className="products-admin-form-section products-admin-form-section--channels">
                                    <h3 className="products-admin-form-section-title">{t("products_modal_channels_heading")}</h3>
                                    <div className="products-admin-form-section-grid">
                                        {CHANNEL_KEYS.map(key => (
                                            <label key={key} className="dispatch-admin-form-label products-admin-form-label--checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={draft[key]}
                                                    onChange={e => setField(key, e.target.checked)}
                                                    disabled={savingEdit}
                                                />
                                                <span>{t("products_modal_sync_to", { channel: CHANNEL_LABELS[key] })}</span>
                                            </label>
                                        ))}
                                    </div>
                                    {/* Push stock button + read-only on-hand display.
                                        Hidden when neither sync flag is on.
                                        The on-hand value shown is the exact number that
                                        will be sent to the channel when Push fires —
                                        `max(0, on_hand - allocated)` summed across
                                        warehouses. Refreshed when the modal opens. */}
                                    {(draft.sync_jobalots || draft.sync_bargainfox) && (
                                        <div className="products-admin-form-push-stock">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm"
                                                disabled={savingEdit || pushingStock || readOnly}
                                                aria-disabled={readOnly}
                                                title={readOnly ? t("read_only_mode") : undefined}
                                                onClick={async () => {
                                                    if (!editing) return;
                                                    setPushingStock(true);
                                                    try {
                                                        const res = await apiFetch(
                                                            `/products/${encodeURIComponent(editing.sku)}/push-stock`,
                                                            { method: "POST" },
                                                        );
                                                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                                                        const data = await res.json().catch(() => ({}));
                                                        toast.success(t("products_modal_push_stock_ok", {
                                                            n: String(data.enqueued ?? 0),
                                                        }));
                                                        // Refresh on-hand + start polling the partner
                                                        // response so the operator can see the result
                                                        // land without leaving the modal.
                                                        await fetchInventorySummary(editing.sku);
                                                        await fetchSyncStatus(editing.sku);
                                                        setPollingSync(true);
                                                    } catch {
                                                        toast.error(t("products_modal_push_stock_err"));
                                                    } finally {
                                                        setPushingStock(false);
                                                    }
                                                }}
                                            >
                                                {pushingStock ? t("loading") : t("products_modal_push_stock")}
                                            </button>
                                            <span className="products-admin-form-push-stock-onhand" title={
                                                inventorySummary && inventorySummary.warehouses.length > 1
                                                    ? inventorySummary.warehouses.map((w) => `${w.warehouse}: ${w.available}`).join(" · ")
                                                    : undefined
                                            }>
                                                {inventorySummary === null
                                                    ? t("loading")
                                                    : t("products_modal_push_stock_value", {
                                                        n: String(inventorySummary.total_available),
                                                    })
                                                }
                                            </span>
                                            <span className="products-admin-form-push-stock-hint">
                                                {t("products_modal_push_stock_hint")}
                                            </span>
                                        </div>
                                    )}
                                    {/* Partner-response panel — one row per (channel,
                                        vendor) carrying the latest stock_sync state.
                                        Status badge colour-codes the row; HTTP code +
                                        error excerpt explain failures inline. Polls
                                        every 2.5s while any row is still pending. */}
                                    {(draft.sync_jobalots || draft.sync_bargainfox) && syncStatus !== null && syncStatus.length > 0 && (
                                        <div className="products-admin-form-sync-status">
                                            <p className="products-admin-form-sync-status-label">
                                                {t("products_modal_sync_status_heading")}
                                            </p>
                                            <ul className="products-admin-form-sync-status-list">
                                                {/* Column header row — same grid as data rows
                                                    so columns align. Aria-hidden because it's
                                                    visual only; the row content carries the
                                                    actual semantic labelling. */}
                                                <li className="products-admin-form-sync-row products-admin-form-sync-row--head" aria-hidden="true">
                                                    <span>{t("products_modal_sync_col_vendor")}</span>
                                                    <span>{t("products_modal_sync_col_warehouse")}</span>
                                                    <span>{t("products_modal_sync_col_qty")}</span>
                                                    <span>{t("products_modal_sync_col_status")}</span>
                                                    <span>{t("products_modal_sync_col_detail")}</span>
                                                </li>
                                                {syncStatus.map((r) => {
                                                    const rowKey = `${r.channel_code}-${r.vendor_key}`;
                                                    const isExpanded = expandedSyncRow === rowKey;
                                                    const hasTrace = r.request_body != null || r.response_body != null;
                                                    return (
                                                    <Fragment key={rowKey}>
                                                        <li className={`products-admin-form-sync-row products-admin-form-sync-row--${r.status}`}>
                                                            <span className="products-admin-form-sync-vendor">
                                                                {r.vendor_name || r.vendor_key} <span className="products-admin-form-sync-channel">({r.channel_name})</span>
                                                            </span>
                                                            <span className="products-admin-form-sync-warehouse">
                                                                {r.warehouse}
                                                            </span>
                                                            <span className="products-admin-form-sync-qty">
                                                                {r.available_quantity}
                                                            </span>
                                                            <span className={`products-admin-form-sync-badge products-admin-form-sync-badge--${r.status}`}>
                                                                {r.status === "delivered" ? t("products_modal_sync_success")
                                                                : r.status === "abandoned" ? t("products_modal_sync_failed")
                                                                : t("products_modal_sync_pending")}
                                                            </span>
                                                            {hasTrace ? (
                                                                <button
                                                                    type="button"
                                                                    className="products-admin-form-sync-expand"
                                                                    title={t("products_modal_sync_view_response")}
                                                                    onClick={() => setExpandedSyncRow(isExpanded ? null : rowKey)}
                                                                >
                                                                    {isExpanded ? "▾" : "▸"}
                                                                </button>
                                                            ) : (
                                                                <span className="products-admin-form-sync-expand products-admin-form-sync-expand--empty" title={t("products_modal_sync_no_trace")}>—</span>
                                                            )}
                                                        </li>
                                                        {r.error && !isExpanded && (
                                                            <li className="products-admin-form-sync-error-row">
                                                                <span className="products-admin-form-sync-error" title={r.error}>
                                                                    {r.error.length > 120 ? `${r.error.slice(0, 117)}…` : r.error}
                                                                </span>
                                                            </li>
                                                        )}
                                                        {isExpanded && (
                                                            <li className="products-admin-form-sync-trace">
                                                                <div className="products-admin-form-sync-trace-section">
                                                                    <p className="products-admin-form-sync-trace-label">
                                                                        {t("products_modal_sync_request")}
                                                                        {r.duration_ms != null && (
                                                                            <span className="products-admin-form-sync-trace-time"> · {r.duration_ms}ms</span>
                                                                        )}
                                                                    </p>
                                                                    <pre className="products-admin-form-sync-trace-body">
                                                                        {typeof r.request_body === "string"
                                                                            ? r.request_body
                                                                            : JSON.stringify(r.request_body, null, 2)}
                                                                    </pre>
                                                                </div>
                                                                <div className="products-admin-form-sync-trace-section">
                                                                    <p className="products-admin-form-sync-trace-label">
                                                                        {t("products_modal_sync_response")}
                                                                        {r.status_code != null && (
                                                                            <span className="products-admin-form-sync-trace-time"> · HTTP {r.status_code}</span>
                                                                        )}
                                                                    </p>
                                                                    <pre className="products-admin-form-sync-trace-body">
                                                                        {typeof r.response_body === "string"
                                                                            ? r.response_body
                                                                            : JSON.stringify(r.response_body, null, 2)}
                                                                    </pre>
                                                                </div>
                                                            </li>
                                                        )}
                                                    </Fragment>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                </section>
                            </div>
                            ) : (
                                <div className="products-admin-logs">
                                    {logsLoading ? (
                                        <div className="products-admin-logs-loading">{t("loading")}</div>
                                    ) : logs.length === 0 ? (
                                        <div className="products-admin-logs-empty">{t("logs_no_matches")}</div>
                                    ) : (
                                        <table className="products-admin-logs-table">
                                            <thead>
                                                <tr>
                                                    <th>{t("logs_col_ts")}</th>
                                                    <th>{t("logs_col_user")}</th>
                                                    <th>{t("logs_col_action")}</th>
                                                    <th>{t("logs_col_status")}</th>
                                                    <th>{t("logs_col_warehouse")}</th>
                                                    <th>{t("logs_col_context")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logs.map(r => {
                                                    const isOpen = expandedLogId === r.id;
                                                    return (
                                                    <Fragment key={r.id}>
                                                        <tr
                                                            className={`logs-row${isOpen ? " logs-row--open" : ""}`}
                                                            onClick={() => setExpandedLogId(isOpen ? null : r.id)}
                                                            style={{ cursor: "pointer" }}
                                                        >
                                                            <td className="logs-cell-ts">{formatLogTs(r.ts)}</td>
                                                            <td>{r.user_name ?? "—"}</td>
                                                            <td className="logs-cell-action">{r.action}</td>
                                                            <td>
                                                                <span className={`logs-status logs-status--${r.status}`}>
                                                                    {r.status}
                                                                </span>
                                                            </td>
                                                            <td>{r.warehouse ?? "—"}</td>
                                                            <td>{renderProductLogContext(r)}</td>
                                                        </tr>
                                                        {isOpen && (
                                                            <tr className="products-admin-logs-detail-row">
                                                                <td colSpan={6}>
                                                                    <pre className="products-admin-logs-metadata">
{JSON.stringify(r.metadata ?? {}, null, 2)}
                                                                    </pre>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-confirm-buttons">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={closeEdit}
                                disabled={savingEdit}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={saveEdit}
                                disabled={savingEdit || readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {savingEdit ? t("saving") : t("save")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsAdmin;
